import { TimesheetDayStatus, OffCrewType } from '../../types';

/** One Off Crew Personnel row in the timesheet grid (Office/Visitor/Manpower) */
export interface OffCrewRow {
  key: string;
  documentId?: string;
  id?: string | number;
  type: OffCrewType;
  name: string;
  days: Record<string, TimesheetDayStatus>;
  description: string;
}

export const offCrewTypeLabels: Record<OffCrewType, string> = {
  office: 'Office',
  visitor: 'Visitor',
  manpower: 'Manpower',
};

export const offCrewNamePlaceholders: Record<OffCrewType, string> = {
  office: 'Office staff name + period on board',
  visitor: 'e.g. عدد 4 افراد ورشة ...',
  manpower: 'Manpower name / assigned works',
};

/** Compact input style for inputs living inside the timesheet tables */
export const tableInputCls =
  'w-full px-2.5 py-1.5 text-sm border border-gray-300 rounded-md bg-white placeholder-gray-400 focus:outline-none focus:ring-2 focus:ring-ocean-500/30 focus:border-ocean-500';

/** Print value per the sheet legend: work = 1, تعيين/رفت = 0.5, off = 0 */
export const dayPrintValue = (status?: TimesheetDayStatus): string =>
  status === 'work' ? '1' : status === 'half' ? '0.5' : '0';

/** "2026-07" -> number of days in that month */
export const daysInMonth = (monthValue: string): number => {
  const [y, m] = monthValue.split('-').map(Number);
  if (!y || !m) return 31;
  return new Date(y, m, 0).getDate();
};

/** "2026-07" -> "2026-07-01" (API date for the timesheet month field) */
export const monthToApiDate = (monthValue: string): string => `${monthValue}-01`;

export const currentMonthValue = (): string => {
  const now = new Date();
  return `${now.getFullYear()}-${String(now.getMonth() + 1).padStart(2, '0')}`;
};

/** Short weekday label for a day of the given month ("2026-07", 4 -> "Sa") */
export const weekdayLabel = (monthValue: string, day: number): string => {
  const [y, m] = monthValue.split('-').map(Number);
  return new Date(y, m - 1, day).toLocaleDateString('en-GB', { weekday: 'narrow' });
};

/** Friday/Saturday — the Egyptian weekend — get a tinted day cell */
export const isWeekend = (monthValue: string, day: number): boolean => {
  const [y, m] = monthValue.split('-').map(Number);
  const wd = new Date(y, m - 1, day).getDay();
  return wd === 5 || wd === 6;
};

/** True when the cell is today's actual date */
export const isToday = (monthValue: string, day: number): boolean => {
  const now = new Date();
  return monthValue === currentMonthValue() && now.getDate() === day;
};

/** "2026-08" -> "August 2026" for card headers */
export const monthLabel = (monthValue: string): string => {
  const [y, m] = monthValue.split('-').map(Number);
  if (!y || !m) return monthValue;
  return new Date(y, m - 1, 1).toLocaleDateString('en-GB', { month: 'long', year: 'numeric' });
};

/** Click cycle: off -> work -> half (تعيين/رفت) -> off */
export const cycleStatus = (status?: TimesheetDayStatus): TimesheetDayStatus => {
  if (status === 'work') return 'half';
  if (status === 'half') return 'off';
  return 'work';
};

/** Sum of days: work = 1, half (تعيين/رفت) = 0.5 */
export const countWorkedDays = (days: Record<string, TimesheetDayStatus> | undefined): number => {
  if (!days) return 0;
  return Object.values(days).reduce((sum, s) => sum + (s === 'work' ? 1 : s === 'half' ? 0.5 : 0), 0);
};

export const statusDisplay: Record<TimesheetDayStatus, { symbol: string; cls: string; title: string }> = {
  work: { symbol: '✓', cls: 'bg-emerald-500 text-white border-emerald-600 shadow-sm', title: 'Worked (full day)' },
  half: { symbol: '½', cls: 'bg-amber-400 text-white border-amber-500 shadow-sm', title: 'تعيين / رفت (half day)' },
  off: { symbol: '', cls: 'bg-white text-gray-300 border-gray-200 hover:border-gray-400', title: 'Not worked' },
};

/** Shared style for the clickable day cells */
export const dayCellBtnCls =
  'w-9 h-9 rounded-md border text-sm font-bold transition-transform duration-75 hover:scale-110 focus:outline-none focus:ring-2 focus:ring-ocean-500/40';
